/**
 * FR-19 — the mileage log.
 *
 * The rate is looked up by the *year of travel*, not the current year. A 2024
 * trip logged in 2026 uses the 2024 rate, and getting that backwards overstates
 * the claim. The form shows which rate it is about to use.
 *
 * When a year has no rate yet, the form asks for it here rather than sending
 * the person to Settings: the IRS publishes one number a year, and the moment
 * it is missing is the moment someone is trying to log a trip.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { claims } from '../auth.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, Money, Notice, PageHead, toast } from '../ui.js';
import type { AppConfigResponse, CoveredPerson } from '../types.js';

export function Mileage() {
  const navigate = useNavigate();
  const config = useApi<AppConfigResponse>('/api/config');
  const persons = useApi<{ persons: CoveredPerson[] }>('/api/persons');
  const isAdmin = (claims()?.['cognito:groups'] ?? []).includes('admin');

  const [tripDate, setTripDate] = useState(new Date().toISOString().slice(0, 10));
  const [purpose, setPurpose] = useState('');
  const [miles, setMiles] = useState('');
  const [patient, setPatient] = useState('');
  const [parking, setParking] = useState('');
  const [newRate, setNewRate] = useState('');

  const people = (persons.data?.persons ?? []).filter((p) => p.active);
  const who = patient || config.data?.config.default_patient_id || '';

  const create = useAction(async () => {
    const res = await api.post<{ expense: { expense_id: string } }>('/api/mileage', {
      trip_date: tripDate,
      purpose,
      round_trip_miles: Number(miles),
      patient_id: who,
      parking_and_tolls_cents: parking ? Math.round(Number(parking) * 100) : 0,
    });
    navigate(`/expenses/${res.expense.expense_id}`);
  });

  const year = tripDate.slice(0, 4);
  const rates = config.data?.config.mileage_rate_by_year ?? {};
  const rate = rates[year];

  const saveRate = useAction(async () => {
    await api.put('/api/config', { mileage_rate_by_year: { ...rates, [year]: Number(newRate) } });
    setNewRate('');
    toast(`${year} rate saved`);
    config.reload();
  });

  const amount =
    rate !== undefined && Number(miles) > 0
      ? Math.round(Number(miles) * rate) + (parking ? Math.round(Number(parking) * 100) : 0)
      : null;

  return (
    <>
      <PageHead title="A drive to medical care" back={{ to: '/add', label: 'Add' }}>
        Miles driven for care count, at the IRS rate for the year of the trip.
      </PageHead>

      <div className="card stack">
        <div className="fields two">
          <div className="field">
            <label htmlFor="date">Date of the trip</label>
            <input id="date" type="date" value={tripDate} onChange={(e) => setTripDate(e.target.value)} />
            {rate !== undefined ? (
              <span className="hint">
                {year} rate: {rate}¢ a mile
              </span>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="patient">Who it was for</label>
            <select id="patient" value={who} onChange={(e) => setPatient(e.target.value)}>
              <option value="">Choose…</option>
              {people.map((p) => (
                <option key={p.person_id} value={p.person_id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {config.data && rate === undefined ? (
          <Notice kind="warn" title={`No mileage rate for ${year} yet`}>
            {isAdmin ? (
              <div className="stack tight">
                <span>Enter the IRS medical mileage rate for {year}, in cents per mile.</span>
                <div className="actions">
                  <input
                    aria-label={`Cents per mile for ${year}`}
                    inputMode="decimal"
                    style={{ width: 120 }}
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                  />
                  <button onClick={() => void saveRate.run()} disabled={saveRate.pending || !(Number(newRate) > 0)}>
                    Save rate
                  </button>
                </div>
                {saveRate.error ? <ErrorBox error={saveRate.error} /> : null}
              </div>
            ) : (
              'The account owner needs to add it before this trip can be logged.'
            )}
          </Notice>
        ) : null}

        <div className="field">
          <label htmlFor="purpose">Where and why</label>
          <input
            id="purpose"
            placeholder="Oncology follow-up, Dr. Reyes"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
          />
        </div>

        <div className="fields two">
          <div className="field">
            <label htmlFor="miles">Round-trip miles</label>
            <input id="miles" inputMode="decimal" value={miles} onChange={(e) => setMiles(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="parking">Parking and tolls</label>
            <input
              id="parking"
              inputMode="decimal"
              placeholder="0.00"
              value={parking}
              onChange={(e) => setParking(e.target.value)}
            />
          </div>
        </div>

        {amount !== null ? (
          <p className="small">
            Claimable: <strong><Money cents={amount} /></strong>
          </p>
        ) : null}

        {create.error ? <ErrorBox error={create.error} /> : null}

        <button
          className="primary big"
          onClick={() => void create.run()}
          disabled={create.pending || rate === undefined || !who || purpose.trim().length < 4 || !(Number(miles) > 0)}
        >
          {create.pending ? 'Saving…' : 'Log the trip'}
        </button>
      </div>
    </>
  );
}
